import { useLocalStorage } from '@vueuse/core'

/** Persisted switch for Chinese-plus-Japanese replies. Default is on. */
export const bilingualReplyStorageKey = 'velis/bilingual-reply'

/**
 * Tells the model to write the visible reply, then the same reply in Japanese.
 * The screen splits the two languages. A short Japanese block is translated again.
 */
export const bilingualReplyInstruction = `Write the reply in the user's language first.
Then leave one blank line.
After that blank line, write the same reply in Japanese.
Use one Japanese sentence for each sentence in the first part.
Write normal Japanese with kanji and kana.
Do not put spaces between Japanese words.
Do not write a label such as 日本語 or 日语.
Do not put a blank line inside the Japanese text.`

/**
 * Tells the model to translate one finished reply.
 * The screen shows this Japanese under the Chinese. Speech reads it.
 */
export const japaneseTranslationInstruction = `Translate the user's message into Japanese.
Keep every sentence.
Do not shorten the message.
Do not add a title or a label.
Do not write tags such as <|ACT|> or <|DELAY|>.
Write normal Japanese with kanji and kana.
Do not put spaces between Japanese words.`

const kana = /[\u3040-\u30ff]/
const marker = /(?:日本語|日语)\s*[:：]\s*/g
const controlTag = /<\|[\s\S]*?\|>/g
const sentenceEnd = /[。！？!?]/g

/**
 * Removes a bilingual label the model may still print.
 *
 * @example
 * stripBilingualMarker('中文。日本語: 今日はいい天気だ。')
 * // => '中文。今日はいい天気だ。'
 */
export function stripBilingualMarker(reply: string) {
  return reply.replace(marker, '')
}

/**
 * Removes action and delay tags from text that is shown or spoken.
 * The stream parser still receives those tags for emotion and motion.
 *
 * @example
 * stripControlTags('<|ACT {"emotion":{"name":"happy"}}|> 今天天气很好。')
 * // => ' 今天天气很好。'
 */
export function stripControlTags(reply: string) {
  return reply.replace(controlTag, '')
}

/**
 * Counts sentences by their ending marks.
 * Text with no ending mark counts as one sentence.
 *
 * @example
 * sentenceCount('外面怎么样？有风吗？')
 * // => 2
 */
export function sentenceCount(text: string) {
  const marks = text.match(sentenceEnd)
  if (marks?.length)
    return marks.length
  return text.trim() ? 1 : 0
}

/**
 * Splits a reply into the Chinese block and the Japanese block.
 * Control tags are removed first. Each line is Chinese, Japanese, or both.
 * Japanese starts at the kana run on that line, including the kanji directly before it.
 *
 * @example
 * splitBilingualReply('今天天气很好。\n\n今日はいい天気だ。')
 * // => { chinese: '今天天气很好。', japanese: '今日はいい天気だ。' }
 */
export function splitBilingualReply(reply: string) {
  const cleaned = stripControlTags(stripBilingualMarker(reply)).trim()
  if (!cleaned)
    return { chinese: '', japanese: '' }

  const chineseLines: string[] = []
  const japaneseLines: string[] = []
  for (const line of cleaned.split(/\n+/)) {
    const kanaAt = line.search(kana)
    if (kanaAt < 0) {
      const chinese = line.trim()
      if (chinese)
        chineseLines.push(chinese)
      continue
    }

    let start = kanaAt
    while (start > 0 && /[\u4e00-\u9fff]/.test(line[start - 1] ?? ''))
      start -= 1

    const chinese = line.slice(0, start).trim()
    const japanese = line.slice(start).trim()
    if (chinese)
      chineseLines.push(chinese)
    if (japanese)
      japaneseLines.push(japanese)
  }

  return {
    chinese: chineseLines.join('\n\n'),
    japanese: japaneseLines.join('\n\n'),
  }
}

/**
 * Returns the Japanese block that speech should read.
 *
 * @example
 * japaneseSpeechText('今天天气很好。今日はいい天気だ。')
 * // => '今日はいい天気だ。'
 */
export function japaneseSpeechText(reply: string) {
  return splitBilingualReply(reply).japanese
}

/**
 * Japanese is complete when it has kana and at least as many sentences as the Chinese.
 *
 * @example
 * japaneseCoversReply('外面怎么样？有风吗？', '外はどんな感じ？')
 * // => false
 */
export function japaneseCoversReply(chinese: string, japanese: string) {
  if (!kana.test(japanese))
    return false
  return sentenceCount(japanese) >= sentenceCount(chinese)
}

/**
 * Returns the two chat blocks. The caller renders each block separately.
 *
 * @example
 * bilingualTextSlices('今天天气很好。', '今日はいい天気だ。')
 * // => [{ type: 'text', text: '今天天气很好。' }, { type: 'text', text: '今日はいい天気だ。' }]
 */
export function bilingualTextSlices(chinese: string, japanese: string) {
  const slices: Array<{ type: 'text', text: string }> = []
  if (chinese.trim())
    slices.push({ type: 'text', text: chinese.trim() })
  if (japanese.trim())
    slices.push({ type: 'text', text: japanese.trim() })
  return slices
}

/**
 * Asks the supplied model call to translate one Chinese reply.
 * The result is only the Japanese block.
 *
 * @example
 * await requestJapaneseTranslation('今天天气很好。', async () => '今日はいい天気だ。')
 * // => '今日はいい天気だ。'
 */
export async function requestJapaneseTranslation(
  chinese: string,
  complete: (instruction: string, source: string) => Promise<string>,
) {
  const translated = stripBilingualMarker(await complete(japaneseTranslationInstruction, chinese)).trim()
  const parts = splitBilingualReply(translated)
  if (parts.japanese)
    return parts.japanese
  return kana.test(translated) ? translated : ''
}

/** Shared on/off switch for bilingual replies and Japanese speech. */
export function useBilingualReply() {
  return useLocalStorage(bilingualReplyStorageKey, true)
}
